import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Text from "@/components/ui/text";
import Association from "@/components/controls/Association";
import FilterTabs from "@/components/ui/FilterTabs";
import { useFilteredDeals } from "@/pages/deals/hooks";
import { useLeadsInfinite } from "@/pages/leads/hooks";
import { useFilteredContracts } from "@/pages/contracts/hooks";
import { LeadData, LeadStatus } from "@/pages/leads/types";
import { DealData, DealStatus } from "@/pages/deals/types";
import { ContractTableRow, ContractTableStatus } from "@/pages/contracts/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { useEntityFields } from "@/pages/settings/hooks";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import { EmptyFilters, Filters } from "@/services/api/types";
import {
  partnerContactContractsFilter,
  partnerContactDealsFilter
} from "@/components/layouts/involvedInFilters";
import { cn } from "@/utils/utils";
import OptionsDropdownMenu from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { Button } from "@/components/ui/button";
import { Add, ChevronDown } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import { IconButton } from "@/components/ui/icon-button";
import Spinner from "@/components/ui/spinner";
import { isEmpty, map } from "lodash";
import { Divider } from "@/components/ui/divider";
import Badge from "@/components/ui/badge";

type InvolvedType = "leads" | "deals" | "contracts";

// Discriminates an association by a field unique to its entity: contracts carry
// `renewalType`, deals carry `stage`, leads carry neither.
const associationTypeOf = (
  association: DealData | LeadData | ContractTableRow
): InvolvedType =>
  "renewalType" in association
    ? "contracts"
    : "stage" in association
      ? "deals"
      : "leads";

interface Props {
  entityId: string;
  entityType:
    | EntityType.PERSON
    | EntityType.COMPANY
    | EntityType.PARTNER_CONTACT
    | EntityType.PARTNER;
  className?: string;
  isQuickView?: boolean;
  onCreateLead?: () => void;
  onCreateDeal?: () => void;
  onCreateContract?: () => void;
  partnerFilters?: {
    leads?: Filters;
    deals?: Filters;
    contracts?: Filters;
  };
}

const InvolvedIn: React.FC<Props> = ({
  entityId,
  entityType,
  className,
  isQuickView = false,
  onCreateLead,
  onCreateDeal,
  onCreateContract,
  partnerFilters
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isPartner = entityType === EntityType.PARTNER;
  const isPartnerContact = entityType === EntityType.PARTNER_CONTACT;
  const fieldByType =
    entityType === EntityType.COMPANY ? "companyId" : "contactId";
  const [isClosedSectionOpen, setIsClosedSectionOpen] = useState(false);
  const [activeType, setActiveType] = useState<InvolvedType>("leads");

  // A partner contact's *primary* link to a deal/lead is a dynamically-keyed field.
  const { data: dealFields, isLoading: isDealFieldsLoading } =
    useEntityFields(EntityType.DEAL);
  const { data: leadFields, isLoading: isLeadFieldsLoading } =
    useEntityFields(EntityType.LEAD);
  const dealPartnerContactKey = dealFields?.fields?.find(
    (field) => field.displayType === FieldDisplayType.PARTNER_CONTACTS
  )?.fieldKey;
  const leadPartnerContactKey = leadFields?.fields?.find(
    (field) => field.displayType === FieldDisplayType.PARTNER_CONTACTS
  )?.fieldKey;

  const dealsFilter = useMemo(() => {
    if (isPartner) return partnerFilters?.deals ?? EmptyFilters;
    if (isPartnerContact)
      return partnerContactDealsFilter(entityId, dealPartnerContactKey);
    return entityType !== EntityType.COMPANY
      ? {
          $or: [
            { [fieldByType]: [entityId] },
            { manuallyAddedPeople: { contains: entityId } },
            { automaticallyAddedPeople: { contains: entityId } }
          ]
        }
      : { [fieldByType]: [entityId] };
  }, [
    isPartner,
    isPartnerContact,
    dealPartnerContactKey,
    partnerFilters,
    fieldByType,
    entityId,
    entityType
  ]);

  const leadsFilter = useMemo(() => {
    if (isPartner) return partnerFilters?.leads ?? EmptyFilters;
    if (isPartnerContact)
      return leadPartnerContactKey
        ? { [leadPartnerContactKey]: [entityId] }
        : EmptyFilters;
    return { [fieldByType]: [entityId] };
  }, [
    isPartner,
    isPartnerContact,
    leadPartnerContactKey,
    partnerFilters,
    fieldByType,
    entityId
  ]);

  const contractsFilter = useMemo(() => {
    if (isPartner) return partnerFilters?.contracts ?? EmptyFilters;
    // The people arrays below hold person ids only, so they never match a contact.
    if (isPartnerContact) return partnerContactContractsFilter(entityId);
    if (entityType !== EntityType.COMPANY) {
      // Contracts have no contactId field — person association is exclusively via
      // manuallyAddedPeople / automaticallyAddedPeople (set on the contract's People tab).
      return {
        $or: [
          { manuallyAddedPeople: { contains: entityId } },
          { automaticallyAddedPeople: { contains: entityId } }
        ]
      };
    }
    return { companyId: [entityId] };
  }, [isPartner, isPartnerContact, partnerFilters, entityId, entityType]);

  // Wait for the field config, not for a key: a contact's array clauses stand alone,
  // but firing early would refetch once the primary-person key resolves.
  const dealsEnabled = isPartnerContact
    ? !isDealFieldsLoading
    : !isPartner || !!partnerFilters?.deals;
  const leadsEnabled = isPartnerContact
    ? !!leadPartnerContactKey
    : !isPartner || !!partnerFilters?.leads;
  const contractsEnabled = !isPartner || !!partnerFilters?.contracts;

  const {
    entities: deals,
    total: dealsTotal,
    isError: isDealsError,
    isLoading: isDealsLoading
  } = useFilteredDeals(
    dealsFilter,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    dealsEnabled
  );

  const {
    entities: leads,
    total: leadsTotal,
    isError: isLeadsError,
    isLoading: isLeadsLoading
  } = useLeadsInfinite(
    leadsFilter,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    leadsEnabled
  );

  const {
    contracts,
    isError: isContractsError,
    isLoading: isContractsLoading
  } = useFilteredContracts(
    undefined,
    undefined,
    contractsFilter,
    "",
    contractsEnabled
  );

  // Contracts count towards loading too, or a record whose only association is a
  // contract flashes the "not involved yet" empty state before its rows arrive.
  const isLoading =
    isDealsLoading ||
    isLeadsLoading ||
    isContractsLoading ||
    (isPartnerContact && (isDealFieldsLoading || isLeadFieldsLoading));

  const allAssociations = useMemo<(DealData | LeadData | ContractTableRow)[]>(
    () => [
      ...(!isDealsError ? (deals ?? []) : []),
      ...(!isLeadsError ? (leads ?? []) : []),
      ...(!isContractsError ? (contracts ?? []) : [])
    ],
    [deals, leads, contracts, isDealsError, isLeadsError, isContractsError]
  );

  // Partners group associations behind a type sub-toggle; other entities show
  // every associated record in a single merged list.
  const visibleAssociations = useMemo(
    () =>
      isPartner
        ? allAssociations.filter(
            (association) => associationTypeOf(association) === activeType
          )
        : allAssociations,
    [allAssociations, isPartner, activeType]
  );

  const closedAssociations = useMemo(
    () =>
      visibleAssociations.filter((association) => {
        if ("renewalType" in association) {
          return (
            association.status === ContractTableStatus.EXPIRED ||
            association.status === ContractTableStatus.NO_SERVICES
          );
        }
        if ("stage" in association) {
          return (
            association.status === DealStatus.CLOSED_LOST ||
            association.status === DealStatus.CLOSED_WON
          );
        }
        // Lead
        return (
          association.status === LeadStatus.DISQUALIFIED ||
          association.status === LeadStatus.CLOSED_QUALIFIED ||
          association.status === LeadStatus.CONVERTED
        );
      }),
    [visibleAssociations]
  );

  const openAssociations = useMemo(
    () =>
      visibleAssociations.filter((item) => !closedAssociations.includes(item)),
    [visibleAssociations, closedAssociations]
  );

  const involvedTabs = useMemo(
    () => [
      { key: "leads", label: t("pages.leads"), count: leadsTotal ?? 0 },
      { key: "deals", label: t("pages.deals"), count: dealsTotal ?? 0 }
    ],
    [t, leadsTotal, dealsTotal]
  );

  const activeTypeCount =
    activeType === "leads" ? (leadsTotal ?? 0) : (dealsTotal ?? 0);

  const emptyInvolvedText = isPartner
    ? t("people.involvedIn.emptyInvolvedPartner")
    : entityType === EntityType.COMPANY
      ? t("people.involvedIn.emptyInvolvedCompany")
      : t("people.involvedIn.emptyInvolvedPerson");

  const notInvolvedText = isPartner
    ? t("people.involvedIn.notInvolvedPartner")
    : entityType === EntityType.COMPANY
      ? t("people.involvedIn.notInvolvedCompany")
      : t("people.involvedIn.notInvolvedPerson");

  const getCreateOptions = () => {
    return {
      lead: onCreateLead || (() => {}),
      deal: onCreateDeal || (() => {}),
      ...(onCreateContract && { contract: onCreateContract })
    };
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-6 bg-surface-elevation1 border-[0.5px] border-border-border1 w-full rounded-xl h-full max-h-[30rem] overflow-auto",
        isQuickView ? "bg-surface-elevation2 p-4" : "bg-surface-elevation1 p-6",
        className
      )}
    >
      <div className="flex justify-between items-center">
        <Text variant={isQuickView ? "lgLabel" : "lgSubtitleH1"}>
          {t("people.involvedIn.title")}
        </Text>
        {!isEmpty(allAssociations) && (
          <OptionsDropdownMenu
            options={getCreateOptions()}
            tPrefix="detailedView.create"
            triggerIcon="add"
            buttonVariant="iconNoBg"
            iconSize="md"
            className="hover:bg-surface-hover-strong"
            iconColor="secondary"
          />
        )}
      </div>
      {isPartner && !isEmpty(allAssociations) && (
        <FilterTabs
          tabs={involvedTabs}
          activeTab={activeType}
          onTabChange={(key) => setActiveType(key as InvolvedType)}
        />
      )}
      {isLoading ? (
        <div className="w-full h-full flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      ) : isEmpty(allAssociations) ? (
        <div className="p-2 w-full h-full flex flex-col items-center justify-center gap-2">
          <Text variant="bodyTertiary">{notInvolvedText}</Text>
          <OptionsDropdownMenu
            options={getCreateOptions()}
            tPrefix="detailedView.create"
            customTrigger={
              <Button variant="tertiary" className="flex gap-1">
                <IconContainer size="5" color="cta">
                  <Add />
                </IconContainer>
                {t("detailedView.createNew")}
              </Button>
            }
          />
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            {openAssociations.length === 0 ? (
              <Text variant="bodyTertiary" className="p-2 self-center">
                {emptyInvolvedText}
              </Text>
            ) : (
              map(openAssociations, (association) => (
                <Association
                  key={association?.id}
                  entity={association}
                  entityType={
                    "renewalType" in association
                      ? EntityType.CONTRACT
                      : "stage" in association
                        ? EntityType.DEAL
                        : EntityType.LEAD
                  }
                  detailedViewEntityType={entityType}
                />
              ))
            )}
          </div>
          {closedAssociations.length > 0 && (
            <>
              <Divider />
              <div className="flex flex-col gap-4">
                <div
                  className="flex gap-2 items-center group w-fit cursor-pointer"
                  onClick={() => setIsClosedSectionOpen(!isClosedSectionOpen)}
                >
                  <IconButton
                    variant="iconNoBg"
                    size="xs"
                    iconClassName={cn(
                      "text-h2 transition-transform duration-200",
                      !isClosedSectionOpen && "-rotate-90"
                    )}
                  >
                    <ChevronDown />
                  </IconButton>
                  <Text
                    variant="bodySecondary"
                    className="text-h2"
                  >{`${t("people.involvedIn.closed")}`}</Text>
                  {closedAssociations.length > 0 && (
                    <Badge className="px-[2px] py-[1px] min-w-5 min-h-5">
                      <Text variant="smBodySecondary">
                        {closedAssociations.length}
                      </Text>
                    </Badge>
                  )}
                </div>
                <div
                  className={cn(
                    "grid transition-[grid-template-rows] duration-200 ease-in-out",
                    isClosedSectionOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  )}
                >
                  <div className="overflow-hidden flex flex-col gap-4">
                    {map(closedAssociations, (association) => (
                      <Association
                        key={association?.id}
                        entity={association}
                        entityType={
                          "renewalType" in association
                            ? EntityType.CONTRACT
                            : "stage" in association
                              ? EntityType.DEAL
                              : EntityType.LEAD
                        }
                        detailedViewEntityType={entityType}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
          {isPartner && activeTypeCount > 0 && (
            <Text
              variant="smLabel"
              className="text-cta cursor-pointer w-fit"
              onClick={() =>
                navigate(`/partners/${entityId}?tab=${activeType}`)
              }
              data-test="involved-in-show-all"
            >
              {t("people.involvedIn.showAll")}
            </Text>
          )}
        </>
      )}
    </div>
  );
};

export default InvolvedIn;
